// Layout — top nav + sidebar + breadcrumb + main content
// Ustava sek. 53.2: breadcrumb (drobeckova navigacia) na vrchu kazdej karty
// Ustava sek. 49: NEO ikona v navbar (bell, sek. 49.5)
// Wrap ToastProvider + ShortcutCheatsheet (vsetko aplikacia potrebuje)
//
// Sidebar menu = zatial placeholder bez akcie (routy pribudnu pri Task #7+)

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { COLORS, RADIUS, TYPO, SPACE, SHADOW, Z_INDEX, BREAKPOINTS, todayLocal } from '../theme.js'
import { ToastProvider, useToast } from './Toast.jsx'
import { ShortcutCheatsheet } from './ShortcutCheatsheet.jsx'
import UndoManager from './UndoManager.jsx'
import { useAuth } from '../auth/AuthContext.jsx'
import ChangeMyPasswordDialog from '../auth/ChangeMyPasswordDialog.jsx'
import ChangeMyPinDialog from '../auth/ChangeMyPinDialog.jsx'   // p23 (16. 9. 2026): zmena vlastného PIN-u
import { usePresence } from '../hooks/usePresence.js'
import NavIcon from './NavIcon.jsx'
import { confirmLeave } from '../hooks/unsavedGuard.js'
import ErrorBoundary from './ErrorBoundary.jsx'
import VersionWatcher from './VersionWatcher.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import UnsavedGuardDialog from './UnsavedGuardDialog.jsx'
import KontrolaIkrosOkno from './KontrolaIkrosOkno.jsx'   // kontrola_ikros_v1: okno pre vedúcu office

// =============================================================
// BREADCRUMB context — kazda stranka moze pridat svoju cestu
// =============================================================
const BreadcrumbContext = createContext({ trail: [], setTrail: () => {} })

export function useBreadcrumb() {
  return useContext(BreadcrumbContext)
}

// =============================================================
// Layout root — ToastProvider + cheatsheet + breadcrumb provider
// =============================================================
// MKsoft sync banner — červený pruh keď Matrix nedostáva dáta z MKsoftu (Mirko 2026-06-27)
function MksoftBanner({ user }) {
  const [info, setInfo] = React.useState(null)
  const role = (user && user.role) || ''
  const canSee = ['admin', 'owner', 'owner_view', 'office_manager'].includes(role)
  React.useEffect(() => {
    if (!canSee) return
    let alive = true
    const check = () => {
      fetch('/api/mksoft/health', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('kamir_token') || '') } })
        .then(r => r.json()).then(j => { if (alive) setInfo(j) }).catch(() => {})
    }
    check()
    const t = setInterval(check, 5 * 60 * 1000)
    return () => { alive = false; clearInterval(t) }
  }, [canSee])
  if (!canSee || !info || !info.stale) return null
  return (
    <div style={{ background: '#dc2626', color: '#fff', padding: '7px 18px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      <NavIcon name="alert" size={18} />
      <span>MKsoft sync nebeží — Matrix nedostáva dáta z MKsoftu. Skontroluj MKsoft počítač v Prešove: je zapnutý, má internet a beží naplánovaná úloha KAMIR_MKSOFT_HODINOVY (kamir_import.py).</span>
    </div>
  )
}

export function Layout({ children, user = null }) {
  const [trail, setTrail] = useState([])
  // menu_v1 (Mirko 29. 9. 2026: „po novom bude hlavné menu vždy zatvorené — ušetrí miesto“): po prihlásení a po obnovení stránky
  // je menu zatvorené (úzky pás s ikonami); nájazd myšou ho dočasne rozbalí, ☰ ho otvorí natrvalo do konca práce v okne.
  const [collapsed, setCollapsed] = useState(() => { try { return sessionStorage.getItem('menu_otvorene') !== '1' } catch (_) { return true } })
  const location = useLocation()
  // plocha_v1 (Mirko 20. 10. 2026: „zbav sa ho, ale nech tam zostane"): Alt+F zbalí hornú lištu aj menu do tenkého pásika,
  // Alt+F alebo klik na pásik ich vráti. Pamätá sa v okne (sessionStorage).
  const [cela, setCela] = useState(() => { try { return sessionStorage.getItem('matrix_cela') === '1' } catch (_) { return false } })
  const prepniCela = () => setCela(c => { const n = !c; try { sessionStorage.setItem('matrix_cela', n ? '1' : '0') } catch (_) {}; return n })
  useEffect(() => {
    const h = (e) => { if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyF') { e.preventDefault(); prepniCela() } }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  }, [])

  return (
    <ToastProvider>
      <BreadcrumbContext.Provider value={{ trail, setTrail }}>
        <div style={{
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: COLORS.page,   // vzhlad_predajne_v1 (61.10): sivé pozadie, na ňom biele karty
          color: COLORS.text,
          fontFamily: TYPO.family,
          fontSize: TYPO.sizes.base
        }}>
          {cela && (
            <div onClick={prepniCela} title="Celá obrazovka — klik alebo Alt+F vráti lištu a menu" style={{ height: 20, background: '#0F172A', color: '#94A3B8', fontSize: TYPO.sizes.xs, display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: `0 ${SPACE.md}px`, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden' }}>
              <span style={{ color: '#fff', fontWeight: TYPO.weights.bold }}>Matrix</span>
              {Array.isArray(trail) && trail.length > 0 && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{trail.map(n => n.label).join(' › ')}</span>}
              <span style={{ flex: 1 }} />
              <span>Alt+F vráti lištu a menu</span>
            </div>
          )}
          {!cela && <Topnav user={user} trail={trail} onCela={prepniCela} sidebarCollapsed={collapsed} onToggleSidebar={() => setCollapsed(c => { const n = !c; try { sessionStorage.setItem('menu_otvorene', n ? '0' : '1') } catch (_) {}; return n })} />}
          <MksoftBanner user={user} />

          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {!cela && <Sidebar collapsed={collapsed} userRole={user && user.role} user={user} />}

            <main style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* plocha_v1: drobčeky sú v hornej lište (Topnav), riadok pod ňou je preč — výška patrí práci */}
              <div style={{ flex: 1, minHeight: 0, padding: SPACE.sm, position: 'relative' }}>
                <ErrorBoundary key={location.pathname} label="stránka">{children}</ErrorBoundary>
              </div>
            </main>
          </div>

          <VozidlaPripomienka user={user} />
          <PredajnaOznam user={user} />
          <ZalohyNaNakup user={user} />
          <ShortcutCheatsheet />
          <UndoManager />
          <VersionWatcher />
          <NovinkyModal />
          <KontrolaIkrosOkno user={user} />   {/* kontrola_ikros_v1 */}
          <UnsavedGuardDialog />
        </div>
      </BreadcrumbContext.Provider>
    </ToastProvider>
  )
}

// =============================================================
// Topnav — fixny top bar
// =============================================================
function BirthdayChip() {
  const [list, setList] = useState([])
  const toast = useToast()
  useEffect(() => {
    let alive = true
    fetch('/api/birthdays/today', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('kamir_token') || '') } })
      .then(r => r.json()).then(d => {
        if (!alive || !Array.isArray(d) || !d.length) return
        setList(d)
        // upozornenie raz denne (Mirko 14.7.)
        const key = 'bday_seen_' + todayLocal()
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1')
          try { toast.info('Dnes má narodeniny: ' + d.map(x => x.name + ' (' + x.vek + ')').join(', ')) } catch (_) {}
        }
      }).catch(() => {})
    return () => { alive = false }
  }, [])
  if (!list.length) return null
  return (
    <a href="/v2/zamestnanci" title={'Dnes má narodeniny: ' + list.map(x => x.name + ' (' + x.vek + ')').join(', ')}
      style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fdf2f8', border: '1.5px solid #db2777', borderRadius: RADIUS.md, padding: '5px 12px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: '#be185d', textDecoration: 'none', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1"/><path d="M2 21h20"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>
      {list.map(x => x.name + ' (' + x.vek + ')').join(', ')}
    </a>
  )
}

// vzhlad_predajne_v3 (20. 10. 2026, ústava 61.10): farby tmavej hornej lišty — jediné miesto, ostatné berie theme.js
const LISTA = { bg: '#0F172A', text: '#E2E8F0', muted: '#94A3B8', line: '#334155', pole: 'rgba(255,255,255,0.06)' }

function Topnav({ user, trail, sidebarCollapsed, onToggleSidebar, onCela }) {   // plocha_v1: trail = drobčeky v lište, onCela = celá obrazovka
  return (
    <header style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACE.lg,
      padding: `0 ${SPACE.md}px`,
      height: 44,   // plocha_v1: 52 → 44
      background: LISTA.bg,   // vzhlad_predajne_v3 (61.10): tmavá lišta ako Predajňa
      borderBottom: 'none',
      boxShadow: SHADOW.sm,
      zIndex: Z_INDEX.topnav
    }}>
      <button
        onClick={onToggleSidebar}
        aria-label="Prepnut bočný panel"
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: 20,
          cursor: 'pointer',
          padding: 6,
          color: LISTA.text
        }}
        title={sidebarCollapsed
          ? 'Hlavné menu — otvoriť. ČO: zoznam všetkých obrazoviek Matrixu. PREČO: po novom je menu zatvorené, aby obrazovka mala viac miesta. KAM: klik otvorí menu do konca práce v tomto okne; stačí aj prejsť myšou po ľavom páse a menu sa ukáže.'
          : 'Hlavné menu — zatvoriť a získať viac miesta. Zatvorené menu ostane úzky pás s ikonami; prejdením myši sa dočasne rozbalí.'}
      >☰</button>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.sm }}>
        <span style={{
          fontSize: TYPO.sizes.lg,
          fontWeight: TYPO.weights.extra,
          color: '#fff',
          letterSpacing: '-0.02em'
        }}>Matrix</span>
        <span style={{ fontSize: TYPO.sizes.sm, color: LISTA.muted }}>v2</span>
      </div>

      {/* plocha_v1: drobčeky (sek. 53.2) v lište namiesto vlastného riadku */}
      {Array.isArray(trail) && trail.length > 0 && (
        <nav aria-label="Drobečková navigácia" style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', fontSize: TYPO.sizes.sm, color: LISTA.muted }}>
          {trail.map((node, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ color: LISTA.line }}>›</span>}
              {node.onClick
                ? <button onClick={node.onClick} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: LISTA.muted, fontSize: 'inherit', fontFamily: 'inherit' }}>{node.label}</button>
                : <span style={{ color: idx === trail.length - 1 ? '#fff' : LISTA.muted, fontWeight: idx === trail.length - 1 ? TYPO.weights.semibold : TYPO.weights.normal, overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.label}</span>}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div style={{ flex: 1 }} />

      {/* TODO: globalny search (Ctrl+F) — pripravene miesto */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.xs,
        padding: '4px 10px',
        background: LISTA.pole,
        border: `1px solid ${LISTA.line}`,
        borderRadius: RADIUS.md,
        color: LISTA.muted,
        fontSize: TYPO.sizes.sm,
        minWidth: 200,
        opacity: 0.6
      }}>
        <NavIcon name="hladat" size={14} />
        <span>Hľadať… (Ctrl+K)</span>
      </div>

      {/* Narodeniny dnes (Mirko 14.7.) */}
      <BirthdayChip />

      {/* NAPADY — pevna karta v top menu → samostatny portal mimo Matrix (Mirko 13.7., ustava 103) */}
      <button
        onClick={() => { const napadyUrl = window.location.protocol === 'https:' ? '/napady/' : ('http://' + window.location.hostname + ':8093'); window.open(napadyUrl, '_blank') }}
        title="Napady na vylepsenie / chyby — portal (otvori sa v novom okne)"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: LISTA.pole,
          border: `1px solid ${LISTA.line}`,
          borderRadius: RADIUS.md,
          padding: '5px 12px',
          fontSize: TYPO.sizes.sm,
          fontWeight: TYPO.weights.bold,
          cursor: 'pointer',
          color: '#FCD34D',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap'
        }}
      >Nápady</button>

      {/* Online indikátor (S3, ústava 53.7) — kto je v systéme */}
      <OnlineIndicator />

      {/* Odkial bezi Matrix (Anton vs forpsi) - Mirko 2026-07-08 */}
      <ServerOrigin />

      {/* NEO bell (ust. 49.5) — zatial placeholder */}
      <button
        aria-label="NEO úlohy"
        style={{
          position: 'relative',
          background: 'transparent',
          border: 'none',
          fontSize: 20,
          cursor: 'pointer',
          padding: 6,
          color: LISTA.text
        }}
        title="NEO (sek. 49) — ROADMAP"
      >
        <NavIcon name="zvonec" size={20} />
      </button>

      <button aria-label="Celá obrazovka" onClick={onCela} title="Celá obrazovka — zbalí lištu aj menu (Alt+F)"
        style={{ background: 'transparent', border: `1px solid ${LISTA.line}`, borderRadius: RADIUS.md, height: 28, padding: '0 10px', fontSize: TYPO.sizes.sm, cursor: 'pointer', color: LISTA.muted, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Celá obrazovka</button>
      <button
        aria-label="Klávesové skratky"
        onClick={() => {
          // Klik na '?' button trigger cheatsheet — emulujeme keypress
          const ev = new KeyboardEvent('keydown', { key: '?', bubbles: true })
          document.dispatchEvent(ev)
        }}
        style={{
          background: 'transparent',
          border: `1px solid ${LISTA.line}`,
          borderRadius: RADIUS.full,
          width: 28, height: 28,
          fontSize: TYPO.sizes.base,
          cursor: 'pointer',
          color: LISTA.muted
        }}
        title="Klávesové skratky (?)"
      >?</button>

      {user && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          paddingLeft: SPACE.md,
          borderLeft: `1px solid ${LISTA.line}`
        }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: '#fff' }}>{user.name || 'User'}</div>
            <div style={{ fontSize: TYPO.sizes.xs, color: LISTA.muted }}>{user.funkcia ? user.funkcia : ({admin:'Majiteľ',owner:'Majiteľ',owner_view:'Majiteľ',office_manager:'Office',foreman:'Majster',accountant:'Back office',service:'Servis',driver:'Vodič',partak:'Parťák',quotes_specialist:'Koordinátor zákaziek / rozpočtár',omietkar:'Omietkár'}[user.role]) || user.role || ''}</div>
          </div>
          {user.role === 'admin' && (
            <button
              onClick={() => { localStorage.setItem('kamir_v2_view', 'mobile'); window.location.reload() }}
              title="Prepnúť na mobilný pohľad"
              style={{
                background: 'transparent',
                border: `1px solid ${LISTA.line}`,
                borderRadius: RADIUS.md,
                padding: '4px 10px',
                fontSize: TYPO.sizes.sm,
                cursor: 'pointer',
                color: LISTA.muted,
                fontFamily: 'inherit'
              }}
            >Mobil</button>
          )}
          <UserPinButton />
          <UserPasswordButton />
          {user.onLogout && (
            <button
              onClick={user.onLogout}
              title="Odhlásiť"
              aria-label="Odhlásiť"
              style={{
                background: 'transparent',
                border: `1px solid ${LISTA.line}`,
                borderRadius: RADIUS.md,
                padding: '4px 10px',
                fontSize: TYPO.sizes.sm,
                cursor: 'pointer',
                color: LISTA.muted,
                fontFamily: 'inherit'
              }}
            >Odhlásiť</button>
          )}
        </div>
      )}
    </header>
  )
}

// =============================================================
// UserPinButton — tlačidlo 'PIN' (Mirko 16. 9. 2026, bod 5: „musím ho vedieť jednoducho zmeniť“)
// Remeselník a parťák PIN nemajú (ústava 63.3), preto tlačidlo nevidia.
// =============================================================
function UserPinButton() {
  const [open, setOpen] = useState(false)
  const { user } = useAuth()
  if (!user || ['partak', 'omietkar', 'poterar', 'maliar'].includes(user.role)) return null
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Zmeniť môj PIN na schvaľovanie"
        style={{
          background: 'transparent',
          border: `1px solid ${LISTA.line}`,
          borderRadius: RADIUS.md,
          padding: '4px 10px',
          fontSize: TYPO.sizes.sm,
          cursor: 'pointer',
          color: LISTA.muted,
          fontFamily: 'inherit'
        }}
      >PIN</button>
      <ChangeMyPinDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

// =============================================================
// UserPasswordButton — tlačidlo 'Heslo' s dialógom (Mirko 2026-05-26)
// =============================================================
function UserPasswordButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Zmeniť moje heslo"
        style={{
          background: 'transparent',
          border: `1px solid ${LISTA.line}`,
          borderRadius: RADIUS.md,
          padding: '4px 10px',
          fontSize: TYPO.sizes.sm,
          cursor: 'pointer',
          color: LISTA.muted,
          fontFamily: 'inherit'
        }}
      >Heslo</button>
      <ChangeMyPasswordDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

// =============================================================
// OnlineIndicator — S3: kto je online a kde (ústava 53.7 multi-user)
// =============================================================
function sectionLabel(pathname) {
  if (pathname === '/') return 'Matrix'   // Mirko 16. 9. 2026: „nepoužívaj Domov“ — názov ako v menu
  if (pathname.startsWith('/partneri')) return 'Partneri'
  if (pathname.startsWith('/kontakty')) return 'Kontakty'
  if (pathname.startsWith('/zakazky')) return 'Zákazky'
  if (pathname.startsWith('/faktury')) return 'Vystavené FA'   // jedna_obrazovka_v1
  if (pathname.startsWith('/dosle-v3')) return 'Došlé FA'
  if (pathname.startsWith('/rozpis')) return 'Rozpis prác'
  if (pathname.startsWith('/dennik')) return 'Denník'
  return 'Matrix'
}

function ServerOrigin() {
  const [o, setO] = React.useState(null)
  React.useEffect(() => {
    let alive = true
    const load = () => fetch('/api/server-origin', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('kamir_token') || '') } }).then(r => r.json()).then(d => { if (alive) setO(d) }).catch(() => {})
    load()
    const t = setInterval(load, 30000)
    return () => { alive = false; clearInterval(t) }
  }, [])
  if (!o) return null
  const forpsi = o.origin === 'forpsi'
  return (
    <span title={'Matrix bezi z: ' + (o.label || o.host)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: TYPO.sizes.sm, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
      background: forpsi ? '#fff4e5' : '#e6f4ea', color: forpsi ? '#b45309' : '#15803d',
      border: '1px solid ' + (forpsi ? '#f5c592' : '#a7d7b8')
    }}>
      <><NavIcon name={forpsi ? 'cloud' : 'home'} size={14} /><span style={{marginLeft:2}}>{forpsi ? 'forpsi' : 'Anton'}</span></>
    </span>
  )
}

function OnlineIndicator() {
  const { user } = useAuth()
  const location = useLocation()
  const online = usePresence(user, sectionLabel(location.pathname))
  const [open, setOpen] = useState(false)

  const count = online.length

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={count === 0 ? 'Iba ja online — sám v systéme' : `${count} používateľov online`}
        aria-label={count === 0 ? "Iba ja" : `${count} používateľov online`}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: COLORS.addBg,
          border: `1px solid ${COLORS.add}`,
          borderRadius: RADIUS.full,
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: TYPO.sizes.sm,
          color: COLORS.add,
          fontWeight: TYPO.weights.semibold
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.add }} />
        {count === 0 ? 'Iba ja' : count + ' online'}
      </button>

      {open && (
        <>
          {/* backdrop — klik mimo zatvorí */}
          <div onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: Z_INDEX.dropdown }} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6,
            background: COLORS.bg, border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.md, boxShadow: SHADOW.lg,
            minWidth: 230, zIndex: Z_INDEX.dropdown + 1, overflow: 'hidden'
          }}>
            <div style={{
              padding: '6px 12px', borderBottom: `1px solid ${COLORS.line}`,
              fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
              color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em'
            }}>{count === 0 ? 'V systéme si len ty' : 'V systéme práve teraz (' + count + ')'}</div>
            {online.map(o => {
              const isSelf = user && o.user_id === user.id
              return (
                <div key={o.user_id} style={{
                  display: 'flex', alignItems: 'center', gap: SPACE.sm,
                  padding: '6px 12px', fontSize: TYPO.sizes.sm,
                  borderBottom: `1px solid ${COLORS.lineSoft}`
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.add, flexShrink: 0 }} />
                  <span style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
                    {o.user_name || '?'}{isSelf ? ' (ja)' : ''}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>{o.section || '—'}</span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// =============================================================
// Sidebar — placeholder menu (routy pridem v Task #7+)
// =============================================================
// MENU_V2_2026 — prehodnotenie (Mirko 2026-05-24):
// • Sklad disabled odstránený (zatiaľ nie je potreba)
// • ZoD nie je samostatný modul — je tab v karte zákazky (Mirko nahráva PDF/foto, nevytvára)
// • NEO/Štatistiky/Dokumenty disabled stále viditeľné, kým sa nezobudia
// menu_v1 (Mirko 29. 9. 2026 „nezabúdaj na hints — opäť nikde“; ústava 12.0/4 + 87.4): čo na obrazovke nájdem a na čo mi je.
// Zatvorené menu ukazuje len ikony, preto je vysvetlivka o to dôležitejšia — bez nej človek nevie, kam klikol.
const HINT_MENU = {
  home: 'Môj deň — tvoj pult: čo dnes musíš, časová os dňa, tabuľa pre všetkých a odovzdanie dňa.',
  zakazky: 'Zákazky — karta každej stavby: termíny, financie, výmery, harmonogram, denník, reklamácie, pripravenosť (Stavba).',
  reklamacie: 'Reklamácie — čo zákazník reklamoval, dokedy to treba vybaviť a v akom je to stave.',
  rozpis: 'Rozpis prác — kto kde pracuje v ktorý deň. Odtiaľto vedia partie ráno, kam ísť.',
  dennik: 'Denník — čo sa na stavbe v ktorý deň urobilo, koľko ľudí tam bolo a prečo vznikol prestoj.',
  dopyty: 'Dopyty — nový záujemca od prvého kontaktu po cenovú ponuku; celá komunikácia je v spise dopytu.',
  sms: 'SMS centrum — správy zákazníkom a partiám; každá odoslaná SMS sa uloží do spisu záznamu.',
  obhliadky: 'Obhliadky — dohodnuté stretnutia na stavbe a podklady, z ktorých vzniká cenová ponuka.',
  kalkulacie: 'Kalkulácie — výpočet ceny z položiek a remesiel; podklad pre cenovú ponuku a výkaz výmer.',
  cp: 'Cenové ponuky — čo sme zákazníkovi poslali, za koľko a či odpovedal.',
  objednavky: 'Objednávky — materiál objednaný u dodávateľov a termíny dodania na stavby.',
  partneri: 'Partneri — firmy a ľudia, s ktorými obchodujeme (zákazníci aj dodávatelia). Vymazaný partner ide na 30 dní do koša.',
  kontakty: 'Kontakty — osoby u partnerov a ich telefóny; jeden register pre celý Matrix, nikde sa nepíšu druhýkrát.',
  plu: 'Katalógy — položky, ceny, technické postupy a sortiment predajne.',
  fa: 'Faktúry — vystavené aj došlé, úhrady a zostatky.',
  'dosle-v3': 'Došlé faktúry a bločky — zaradenie na zákazku alebo réžiu; bločky zaraďuje a do MKsoftu posiela Štefánia.',
  predajna: 'Predajňa a Eshop — predaj materiálu, objednávky zákazníkov a sklad.',
  pokladna: 'Pokladňa — hotovosť firmy: príjmové a výdavkové doklady, bločky a vklady.',
  mzdy2026: 'Mzdy — nároky, zálohy a výplaty; podklad pre účtovníčku.',
  zadrzne: 'Zádržné — časť ceny zadržaná objednávateľom a termín jej uvoľnenia.',
  zamestnanci: 'Zamestnanci — karta človeka: funkcia, telefón, partia; funkciu odtiaľto číta celý Matrix.',
  partie: 'Partie — zloženie pracovných čiat a ich parťáci.',
  manualy: 'Manuály pozícií — čo má ktorá funkcia robiť. Z manuálu sa skladá obrazovka Môj deň.',
  dochadzka: 'Dochádzka — príchody a odchody hodinovkárov, podklad pre mzdy.',
  pozicovna: 'Požičovňa — náradie a stroje: kto si čo vzal, kedy to vráti a záloha.',
  vozidla: 'Vozový park — vozidlá, termíny STK, diaľničné známky a poistenie.',
  wip: 'WIP — rozpracovaná výroba: koľko je na stavbách odrobené a ešte nevyfakturované.',
  trinity: 'Trinity — pomocník, ktorý stráži čísla firmy a upozorňuje na odchýlky.',
  staty: 'Štatistiky — prehľady a reporty za obdobie.',
  doks: 'Dokumenty — listy, protokoly a záznamy Matrixu vrátane odovzdaní dňa.',
  nastav: 'Nastavenia — firma, e-mail, číselníky a práva; mení ich admin.',
}

const SIDEBAR_ITEMS = [
  { group: 'Prehľad', items: [
    { id: 'home',        icon: '', label: 'Môj deň',        to: '/' },   /* mojden_v2 (Mirko 28. 9. 2026: „Môj deň v Matrixe“): karta domova sa volá ako obrazovka (64.1) */
  ]},
  { group: 'Zákazky', items: [
    { id: 'zakazky',     icon: '', label: 'Zákazky',        to: '/zakazky' },
    /* 7. 10. 2026: položka dostala roly, aby sa nezobrazovala tomu, komu server vráti 403 —
       zoznam je KÓPIA `ALLOWED` v backend/modules/reklamacie_api.js. Kto má modul zaškrtnutý
       v Používatelia → Individuálne práva, uvidí ju aj bez roly (filter nižšie). */
    { id: 'reklamacie',  icon: '', label: 'Reklamácie',     to: '/reklamacie', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'foreman'] },
    { id: 'rozpis',      icon: '', label: 'Rozpis prác',    to: '/rozpis' },
    { id: 'dennik',      icon: '', label: 'Denník',         to: '/dennik' },
  ]},
  { group: 'Komercia', items: [
    { id: 'dopyty',     icon: '', label: 'Dopyty',         to: '/dopyty' },
    { id: 'sms',        icon: '', label: 'SMS',            to: '/sms' },
    { id: 'obhliadky',  icon: '', label: 'Obhliadky',      to: '/obhliadky' },
    { id: 'kalkulacie',  icon: '', label: 'Kalkulácie',     to: '/kalkulacie', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant', 'quotes_specialist'] },   /* hist_menu_v1 28. 9. 2026: koordinátorka zákaziek / rozpočtárka (ústava 3.S.1, server prava_v1) */   /* p71 20. 9. 2026: z Riadenia do Komercie — patrí do lievika medzi Obhliadku a Cenovú ponuku (Mirko) */
    { id: 'cp',          icon: '', label: 'Cenové ponuky',  to: '/cenove' },
    { id: 'objednavky',  icon: '', label: 'Objednávky',     to: '/objednavky' },
    { id: 'partneri',    icon: '', label: 'Partneri',       to: '/partneri' },
    { id: 'kontakty',    icon: '', label: 'Kontakty',       to: '/kontakty' },   // p93 — kontaktné osoby partnerov (131.10)
    { id: 'plu',         icon: '', label: 'Katalógy',       to: '/katalog' },
  ]},
  { group: 'Financie', items: [
    { id: 'dosle-v3',    icon: '', label: 'Došlé FA',       to: '/dosle-v3', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant'] },   /* V3 131.1 bod 4; jedna_obrazovka_v1 18. 10. 2026: jediná obrazovka došlých FA a bločkov (0.J) */
    { id: 'fa',          icon: '', label: 'Vystavené FA',   to: '/faktury', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant'] },   /* p22 16. 9. 2026: ústava 14.5, 17.6 — server stráži to isté */
    { id: 'predajna',    label: 'Predajňa & Eshop', to: '/predajna' },
    { id: 'pokladna',    icon: '', label: 'Pokladňa',       to: '/pokladna', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant'] },   /* p22 16. 9. 2026: ústava 11.12 — server stráži to isté */
    { id: 'mzdy2026',    icon: '', label: 'Mzdy',           to: '/mzdy2026' },  /* jediné mzdy — starý modul odstránený 18.7. (Mirko: nechcem 2x mzdy) */
    { id: 'zadrzne',     icon: '', label: 'Zádržné',        to: '/zadrzne' },
  ]},
  { group: 'Ľudia', items: [
    { id: 'zamestnanci', icon: '', label: 'Zamestnanci',    to: '/zamestnanci' },
    { id: 'partie',      icon: '', label: 'Partie',         to: '/partie' },
    { id: 'manualy',     icon: '', label: 'Manuály pozícií', to: '/manualy-pozicii', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant'] },   /* mojden_v1 28. 9. 2026: register manuálov (3.S.1); zamestnanec svoj otvára z Môj deň */
    { id: 'dochadzka',   icon: '', label: 'Dochádzka',      to: '/dochadzka', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant'] },
  ]},
  { group: 'Majetok', items: [
    { id: 'pozicovna',   icon: '', label: 'Požičovňa',      to: '/pozicovna' },
    { id: 'vozidla',     icon: '', label: 'Vozový park',    to: '/vozidla' },
  ]},
  { group: 'Riadenie', items: [
    { id: 'wip',         icon: '', label: 'WIP',            to: '/wip' },
    { id: 'trinity',     icon: '', label: 'Trinity',        to: '/trinity' },
    { id: 'staty',       icon: '', label: 'Štatistiky',     to: '/staty' },
    { id: 'doks',        icon: '', label: 'Dokumenty',      to: '/dokumenty' },
    { id: 'prepojenia-mksoft', icon: '', label: 'Prepojenia s MKsoftom', to: '/prepojenia-mksoft', roles: ['admin', 'owner', 'owner_view', 'office_manager', 'accountant', 'quotes_specialist'] },   /* MKSOFT_VAZBY_V1 */
    { id: 'nastav',      icon: '',  label: 'Nastavenia',     to: '/nastavenia' },
  ]},
]

function Sidebar({ collapsed, userRole, user }) {
  // 7. 10. 2026: individuálne práva („Používatelia -> Individuálne práva", nav_permissions.extra_modules)
  // sa v bočnom paneli DOSIAĽ NEVYHODNOCOVALI vôbec — zaškrtnutie modulu nemalo na desktope žiadny
  // účinok, hoci nápoveda v tej obrazovke tvrdí opak. Filter ich teraz berie, ale LEN ako pridanie:
  // `hidden_modules` sa tu zámerne NEaplikuje, takže nikomu nemôže zmiznúť nič, čo dnes vidí.
  const extraModuly = (() => {
    try {
      const np = user && user.nav_permissions
        ? (typeof user.nav_permissions === 'string' ? JSON.parse(user.nav_permissions) : user.nav_permissions)
        : null
      return Array.isArray(np && np.extra_modules) ? np.extra_modules : []
    } catch (_) { return [] }
  })()
  const navigate = useNavigate()
  const location = useLocation()
  const [hover, setHover] = useState(false)
  const expanded = !collapsed || hover
  const isActive = (to) => {
    if (!to) return false
    if (to === '/') return location.pathname === '/'
    return location.pathname === to || location.pathname.startsWith(to + '/')
  }
  return (
    <div style={{ width: collapsed ? 56 : 240, flexShrink: 0, position: 'relative', zIndex: 50 }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <aside style={{
        width: expanded ? 240 : 56,
        height: '100%',
        position: (collapsed && hover) ? 'absolute' : 'static',
        top: 0, bottom: 0, left: 0,
        background: COLORS.bg,
        borderRight: `1px solid ${COLORS.line}`,
        boxShadow: (collapsed && hover) ? '2px 0 16px rgba(0,0,0,0.18)' : 'none',
        overflow: 'auto', display: 'flex', flexDirection: 'column',
        transition: 'width 0.12s ease',
      }}>
        <div style={{ height: 4, background: COLORS.brand, flexShrink: 0 }} />
        <div style={{ padding: `${SPACE.md}px 0`, flex: 1 }}>
          {SIDEBAR_ITEMS.map((group, gIdx) => (
            <div key={group.group} style={{
              marginBottom: SPACE.md, paddingBottom: SPACE.sm,
              borderBottom: gIdx < SIDEBAR_ITEMS.length - 1 ? `1px solid ${COLORS.line}` : 'none',
            }}>
              {expanded && <div style={{
                padding: `${SPACE.xs}px ${SPACE.md}px ${SPACE.xs}px`,
                fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted,   // vzhlad_predajne_v3
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: SPACE.xs,
              }}>{group.group}</div>}
              {group.items.filter(it => !it.roles || it.roles.includes(userRole) || extraModuly.includes(it.id)).map(it => {
                const active = isActive(it.to)
                return (
                  <button key={it.id} disabled={it.disabled}
                    onClick={() => { if (it.to && !it.disabled) confirmLeave().then(ok => { if (ok) navigate(it.to) }) }}
                    onMouseEnter={(e) => { if (!it.disabled && !active) e.currentTarget.style.background = COLORS.bgSoft }}
                    onMouseLeave={(e) => { if (!it.disabled && !active) e.currentTarget.style.background = 'transparent' }}
                    title={HINT_MENU[it.id] || it.label}
                    style={{
                      display: 'flex', alignItems: 'center', gap: SPACE.sm, width: '100%',
                      padding: expanded ? `8px ${SPACE.md}px` : '8px 0',
                      justifyContent: expanded ? 'flex-start' : 'center',
                      fontSize: TYPO.sizes.base * 1.05,
                      color: it.disabled ? COLORS.disabled : active ? COLORS.nav : COLORS.text,
                      background: active ? COLORS.navBg : 'transparent',   // vzhlad_predajne_v3 (61.10): vybraté = modré   // vzhlad_v2: značku drží 4 px pásik vľavo, nie plocha
                      borderLeft: `4px solid ${active ? COLORS.nav : 'transparent'}`,
                      border: 'none', borderRadius: 0,
                      cursor: it.disabled ? 'not-allowed' : 'pointer', opacity: it.disabled ? 0.45 : 1,
                      fontFamily: 'inherit', fontWeight: active ? TYPO.weights.bold : TYPO.weights.normal,
                      textAlign: 'left', transition: 'background 0.12s ease',
                      whiteSpace: 'nowrap', overflow: 'hidden',
                    }}>
                    <span style={{ width: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><NavIcon name={it.id} /></span>
                    {expanded && <span style={{ flex: 1 }}>{it.label}</span>}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </aside>
    </div>
  )
}

// =============================================================
// Breadcrumb — drobeckova navigacia (sek. 53.2)
// =============================================================
function Breadcrumb({ trail }) {
  if (!trail || trail.length === 0) return null
  return (
    <nav
      aria-label="Drobečková navigácia"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.xs,
        padding: `${SPACE.sm}px ${SPACE.xl}px`,
        background: COLORS.bg,
        borderBottom: `1px solid ${COLORS.lineSoft}`,
        fontSize: TYPO.sizes.sm,
        color: COLORS.muted
      }}
    >
      {trail.map((node, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <span style={{ color: COLORS.disabled }}>›</span>}
          {node.onClick ? (
            <button
              onClick={node.onClick}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: COLORS.nav,
                fontSize: 'inherit',
                fontFamily: 'inherit',
                textDecoration: 'none'
              }}
            >{node.label}</button>
          ) : (
            <span style={{ color: idx === trail.length - 1 ? COLORS.text : COLORS.muted, fontWeight: idx === trail.length - 1 ? TYPO.weights.semibold : TYPO.weights.normal }}>
              {node.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}

export default Layout

// ── ČO JE NOVÉ (Mirko 21.7.) — po prihlásení ukáž laickou rečou posledné zmeny; raz na používateľa ──
function NovinkyModal() {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!user) return
    let alive = true
    fetchAuth('/api/novinky/unseen').then(r => r.ok ? r.json() : []).then(d => { if (alive && Array.isArray(d)) setItems(d) }).catch(() => {})
    return () => { alive = false }
  }, [user && (user.id || user.username)])
  if (!user || !items.length) return null
  const zavri = async () => {
    if (busy) return
    setBusy(true)
    try { await fetchAuth('/api/novinky/seen', { method: 'POST' }) } catch (_) {}
    setItems([])
    setBusy(false)
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(560px, 94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid #eef2f7' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#1e293b' }}>Čo je nové v Matrixe</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Krátke zhrnutie zmien — po prečítaní klikni Rozumiem, viac sa neukáže.</div>
        </div>
        <div style={{ padding: '10px 20px', overflowY: 'auto' }}>
          {items.map(n => (
            <div key={n.id} style={{ padding: '9px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: COLORS.brand, fontWeight: 700, flexShrink: 0 }}>{String(n.datum || '').split('-').reverse().join('.')}</span>
                <span style={{ fontWeight: 700, fontSize: 13.5, color: '#1e293b' }}>{n.title}</span>
              </div>
              <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.55, marginTop: 2 }}>{n.text}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={zavri} disabled={busy} style={{ padding: '9px 22px', border: 'none', background: COLORS.nav, color: '#fff', borderRadius: RADIUS.md, cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit', fontSize: 13.5 }}>
            {busy ? 'Ukladám…' : 'Rozumiem'}
          </button>
        </div>
      </div>
    </div>
  )
}

// p47 (Mirko 18. 9. 2026: „dal by som to aj ako widget po zapnutí Matrixu, iba pre office a Kašubjakovú,
// že sa nepohne ďalej, kým vedome neodklikne, že vie, že sa blíži koniec STK a diaľničnej“):
// okno sa ukáže vedúcej office a účtovníčke, kým ho v ten deň nepotvrdia; potvrdenie sa zapíše (kto a kedy).
// p56 (Mirko 18. 9. 2026): keď office ukončí zákazku alebo urobí vyúčtovanie a z požičovne je na nej ešte niečo vonku,
// predajňa dostane správu na stred obrazovky. Zostane tam, kým ju predajňa sáma nezruší. Ak niet čo vrátiť, nič nevyskočí.
// ústava 135.5 (Mirko 19. 9. 2026: „záloha je evidovaná a svieti widget, aby nás nikto neoklamal a majitelia mali prehľad“):
// dve čísla — počet otvorených záloh na nákup a ich celková výška. Nie je to blokujúce okno, je to trvalý prehľad.
// Mirko 2026-09-20: „ako ťa napadlo to dať skrz celý Matrix?" Widget patrí IBA na úvodnú obrazovku
// (Matrix) a má byť MENŠÍ než ostatné. Vydávať zálohu smie len ten, kto má právo na pokladňu —
// to stráži server (/api/zalohy-nakup vracia `smiem`), tu sa widget iba nezobrazí.
function ZalohyNaNakup({ user }) {
  // p98 (Mirko 21. 9. 2026: „Zálohy na nákup musí byť tlačidlo v pokladni"): widget už len INFORMUJE (ústava 0.W) —
  // vydanie aj vrátenie zálohy je v Pokladni (tlačidlo Zálohy na nákup), smie ho len vedúca office / admin / majiteľ.
  const lenDoma = useLocation().pathname === '/'
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [otvorene, setOtvorene] = useState(false)
  useEffect(() => { if (user) fetchAuth('/api/zalohy-nakup').then(r => r.ok ? r.json() : null).then(d => { if (d) setData(d) }).catch(() => {}) }, [user && user.id])
  if (!lenDoma || !data || !data.smiem) return null
  const pocet = data.pocet || 0
  const suma = Number(data.suma || 0)
  return (
    <>
      <button onClick={() => setOtvorene(true)}
        title={'Zálohy na nákup — hotovosť vydaná dopredu, ku ktorej ešte neprišiel bloček. ' + (pocet ? pocet + ' otvorených za ' + suma.toFixed(2) + ' €.' : 'Žiadna otvorená.') + ' Vydávajú a vracajú sa v Pokladni.'}
        style={{ position: 'fixed', right: 14, bottom: 14, zIndex: 4000, display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999, border: '1px solid ' + (pocet ? '#b45309' : '#e2e8f0'),
          background: pocet ? '#fff7ed' : '#fff', color: pocet ? '#b45309' : '#94a3b8', cursor: 'pointer',
          fontFamily: 'inherit', fontWeight: 700, fontSize: 11, boxShadow: '0 2px 8px rgba(15,23,42,.08)', opacity: pocet ? 1 : .75 }}>
        <span style={{ fontWeight: 600 }}>Zálohy</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pocet}</span>
        <span style={{ opacity: .5 }}>·</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{suma.toFixed(0)} €</span>
      </button>
      {otvorene && (
        <div role="dialog" aria-modal="true" aria-label="Zálohy na nákup" onClick={() => setOtvorene(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(640px, 96vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 22px 10px', borderBottom: '1px solid #eef2f7' }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Zálohy na nákup — {pocet} otvorených, {suma.toFixed(2)} €</div>
              <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
                Hotovosť vydaná na nákup, ku ktorej ešte nie je bloček. Peniaze už z pokladne odišli — bloček zálohu uzavrie a vyrovná sa iba rozdiel.
              </div>
            </div>
            <div style={{ padding: '8px 22px', overflowY: 'auto' }}>
              {pocet === 0 ? <div style={{ padding: '12px 0', color: '#15803d', fontWeight: 700 }}>Všetky zálohy sú vyúčtované.</div>
                : (data.polozky || []).map(z => (
                  <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ flex: 1, fontWeight: 700 }}>{z.osoba}</span>
                    <span style={{ flex: 1, color: '#475569' }}>{z.ucel}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{Number(z.suma).toFixed(2)} €</span>
                    <span style={{ fontSize: 12, color: z.dni > 7 ? '#b91c1c' : '#64748b', minWidth: 74, textAlign: 'right' }}>
                      {z.dni === 0 ? 'dnes' : 'pred ' + z.dni + ' dňami'}
                    </span>
                  </div>
                ))}
            </div>
            <div style={{ padding: '12px 22px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => setOtvorene(false)} style={{ padding: '9px 18px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Zavrieť</button>
              <button onClick={() => { setOtvorene(false); navigate('/pokladna') }}
                title="Zálohu vydáva a vracia vedúca office v Pokladni (počas jej dovolenky admin alebo majiteľ)"
                style={{ padding: '9px 20px', border: 'none', background: '#b45309', color: '#fff', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>
                Otvoriť v Pokladni
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function PredajnaOznam({ user }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!user) return
    fetchAuth('/api/predajna/oznam').then(r => r.ok ? r.json() : null).then(setData).catch(() => {})
  }, [user && user.id])
  if (!data || !data.pre_mna || !(data.oznamy || []).length) return null
  const o = data.oznamy[0]
  const zrus = async () => {
    setBusy(true)
    try {
      const r = await fetchAuth('/api/predajna/oznam/' + o.id + '/potvrdit', { method: 'POST' })
      if (r.ok) setData(d => ({ ...d, oznamy: (d.oznamy || []).filter(x => x.id !== o.id) }))
    } catch (_) {} finally { setBusy(false) }
  }
  return (
    <div role="dialog" aria-modal="true" aria-label="Požičovnňa — nevrátené veci na zákazke" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(700px, 96vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ padding: '16px 22px 10px', borderBottom: '1px solid #eef2f7' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#b45309' }}>Požičovňa — na tejto zákazke je ešte niečo vonku</div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
            Zákazka <b>{o.project_id}</b>{o.zakazka_nazov ? ' · ' + o.zakazka_nazov : ''} — {o.dovod}
            {o.kto ? ' (' + o.kto + ')' : ''}. Skontroluj, čo sa má vrátiť, a potom správu zruš.
            {o.vratene_medzitym > 0 ? ' Medzitým sa už vrátilo ' + o.vratene_medzitym + '.' : ''}
          </div>
        </div>
        <div style={{ padding: '8px 22px', overflowY: 'auto' }}>
          {(o.polozky || []).length === 0 ? <div style={{ padding: '10px 0', color: '#15803d', fontWeight: 700 }}>Medzitým sa vrátilo všetko — správu môžeš zrušiť.</div>
            : (o.polozky || []).map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ flex: 1, color: '#334155', fontWeight: 700 }}>{p.polozka}{p.kod ? ' · ' + p.kod : ''}</span>
                <span style={{ flex: 1, color: '#475569' }}>{p.osoba || '—'}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: '#475569' }}>od {String(p.od || '').split('-').reverse().join('.')}</span>
                <span style={{ minWidth: 76, textAlign: 'right', fontWeight: 700, color: p.dni > 30 ? '#b91c1c' : '#b45309' }}>{p.dni} dní</span>
              </div>
            ))}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Zrušenie sa zapíše s tvojím menom a časom.{data.oznamy.length > 1 ? ' Čaká ešte ' + (data.oznamy.length - 1) + '.' : ''}</span>
          <a href={'/pozicovna/reporty?zakazka=' + encodeURIComponent(o.project_id)} style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', textDecoration: 'none' }}>Otvoriť report požičovne</a>
          <button onClick={zrus} disabled={busy} style={{ padding: '10px 24px', border: 'none', background: '#b45309', color: '#fff', borderRadius: 9, cursor: busy ? 'default' : 'pointer', fontWeight: 700, fontFamily: 'inherit', fontSize: 14 }}>
            {busy ? 'Ukladám…' : 'Zrušiť správu'}
          </button>
        </div>
      </div>
    </div>
  )
}

function VozidlaPripomienka({ user }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!user) return
    fetchAuth('/api/vozidla-pripomienka').then(r => r.ok ? r.json() : null).then(setData).catch(() => {})
  }, [user && user.id])
  if (!data || !data.pre_mna || data.potvrdene || !(data.polozky || []).length) return null
  const potvrd = async () => {
    setBusy(true)
    try {
      const r = await fetchAuth('/api/vozidla-pripomienka/potvrdit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kluc: data.kluc }) })
      if (r.ok) setData(d => ({ ...d, potvrdene: true }))
    } catch (_) {} finally { setBusy(false) }
  }
  const poTermine = data.polozky.filter(p => p.expirovane).length
  return (
    <div role="dialog" aria-modal="true" aria-label="Termíny vozidiel" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(680px, 96vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ padding: '16px 22px 10px', borderBottom: '1px solid #eef2f7' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#b91c1c' }}>Termíny vozidiel — STK a diaľničná známka</div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
            {poTermine > 0 ? poTermine + ' po termíne · ' : ''}{data.polozky.length - poTermine} do {data.dni} dní. Prezri si zoznam a potvrď, že o tom vieš — bez potvrdenia sa v Matrixe nepokračuje.
          </div>
        </div>
        <div style={{ padding: '8px 22px', overflowY: 'auto' }}>
          {data.polozky.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, minWidth: 92 }}>{p.spz || '—'}</span>
              <span style={{ flex: 1, color: '#334155' }}>{p.vozidlo} · {p.typ}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{String(p.datum).split('-').reverse().join('.')}</span>
              <span style={{ minWidth: 104, textAlign: 'right', fontWeight: 700, color: p.expirovane ? '#b91c1c' : '#b45309' }}>
                {p.expirovane ? 'po termíne' : (p.dni <= 0 ? 'dnes' : 'o ' + p.dni + ' dní')}
              </span>
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Potvrdenie sa zapíše s tvojím menom a časom. Zajtra sa okno ukáže znova, kým termíny trvajú.</span>
          <a href="/vozidla/terminy" style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', textDecoration: 'none' }}>Otvoriť zoznam vozidiel</a>
          <button onClick={potvrd} disabled={busy} style={{ padding: '10px 24px', border: 'none', background: '#b91c1c', color: '#fff', borderRadius: 9, cursor: busy ? 'default' : 'pointer', fontWeight: 700, fontFamily: 'inherit', fontSize: 14 }}>
            {busy ? 'Ukladám…' : 'Beriem na vedomie'}
          </button>
        </div>
      </div>
    </div>
  )
}
